<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { inventoryApi, type InventoryAlert } from '@/api/inventory';
import { useResponsiveTable } from '@/composables/useResponsiveTable';

const props = defineProps<{ patientId?: number }>();

const status = ref<'open' | 'resolved'>('open');
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const { data: alerts, isPending } = useQuery({
  queryKey: ['inventoryAlerts', computed(() => props.patientId), status],
  queryFn: () => inventoryApi.alerts({ patientId: props.patientId, status: status.value }),
  initialData: [],
});

const headers = computed(() => [
  { title: 'Alerta', key: 'humanKind', sortable: false },
  { title: 'Artículo', key: 'itemName' },
  { title: 'Ubicación', key: 'locationName' },
  { title: 'Desde', key: 'openedAt' },
  status.value === 'open' ?
    { title: 'Aviso', key: 'notifiedAt', sortable: false } :
    { title: 'Resuelta', key: 'resolvedAt' },
]);

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('es-CL');
}

function goToPatient(alert: InventoryAlert) {
  window.location.href = `/patients/${alert.patientId}?tab=inventory`;
}
</script>
<template>
  <div class="pa-4">
    <p class="mb-2 text-body-2 text-medium-emphasis">
      Una alerta se abre al llegar al stock mínimo de una ubicación, o cuando un equipo requiere
      atención o queda fuera de servicio (también si lo reporta un profesional desde la app). Se
      avisa por correo una vez y queda activa hasta que se resuelve.
    </p>
    <v-tabs
      v-model="status"
      color="primary"
      class="mb-2"
    >
      <v-tab
        value="open"
        prepend-icon="mdi-bell-alert"
      >
        Activas
      </v-tab>
      <v-tab
        value="resolved"
        prepend-icon="mdi-history"
      >
        Historial
      </v-tab>
    </v-tabs>
    <v-data-table
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="alerts"
      :loading="isPending"
      loading-text="Cargando alertas..."
      :no-data-text="status === 'open' ? 'No hay alertas activas' : 'No hay alertas resueltas'"
      density="comfortable"
    >
      <template #[`item.humanKind`]="{ item }">
        <v-chip
          :color="item.kind === 'low_stock' ? 'warning' : 'error'"
          size="small"
        >
          {{ item.humanKind }}
        </v-chip>
      </template>
      <template #[`item.itemName`]="{ item }">
        {{ item.itemName }}
        <span
          v-if="item.unitIdentifier"
          class="text-medium-emphasis"
        >· {{ item.unitIdentifier }}</span>
        <div
          v-if="item.note"
          class="text-body-2 text-medium-emphasis"
        >
          «{{ item.note }}»<template v-if="item.reportedBy">
            — {{ item.reportedBy }}
          </template>
        </div>
      </template>
      <template #[`item.locationName`]="{ item }">
        <span
          v-if="item.patientId"
          class="cursor-pointer font-weight-medium"
          role="button"
          tabindex="0"
          @click="goToPatient(item)"
          @keyup.enter="goToPatient(item)"
        >{{ item.locationName }}</span>
        <span v-else>{{ item.locationName }}</span>
      </template>
      <template #[`item.openedAt`]="{ item }">
        {{ formatDate(item.openedAt) }}
      </template>
      <template #[`item.notifiedAt`]="{ item }">
        <v-chip
          size="small"
          variant="tonal"
        >
          {{ item.notifiedAt ? 'Correo enviado' : 'Pendiente' }}
        </v-chip>
      </template>
      <template #[`item.resolvedAt`]="{ item }">
        {{ item.resolvedAt ? formatDate(item.resolvedAt) : '—' }}
      </template>
    </v-data-table>
  </div>
</template>
